import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertTriangle, Copy } from 'lucide-react';
import { Button, CopyableId } from '@fiado/react-kit';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { buildApiLoginCurl } from '../../lib/apiUserLogin';
import type { ApiUserCredentialsLocal } from '../../api/types/TenantLocal';

interface Props {
    /** null ⇒ cerrado. El padre lo pone en null al confirmar; nunca se guarda fuera de ese estado. */
    credentials: ApiUserCredentialsLocal | null;
    tenantId: string;
    /** Nombre del sistema, para el título. */
    systemName?: string;
    onConfirm: () => void;
}

async function copyToClipboard(value: string): Promise<void> {
    try {
        await navigator.clipboard.writeText(value);
    } catch {
        // Sin portapapeles el valor sigue visible y seleccionable.
    }
}

/** Credenciales de un usuario de API, mostradas una sola vez: no se cierra sin confirmar que se guardaron. */
export function ApiUserCredentialsModal({ credentials, tenantId, systemName, onConfirm }: Props) {
    const [saved, setSaved] = useState(false);
    const blockDismiss = (e: Event) => {
        e.preventDefault();
    };

    return (
        <DialogPrimitive.Root open={credentials !== null}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/25 backdrop-blur-sm" />
                <DialogPrimitive.Content
                    className="fixed left-[50%] top-[50%] z-50 flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-3xl translate-x-[-50%] translate-y-[-50%] flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-2xl"
                    onEscapeKeyDown={blockDismiss}
                    onPointerDownOutside={blockDismiss}
                    onInteractOutside={blockDismiss}
                >
                    <DialogPrimitive.Title className="flex shrink-0 items-center gap-2 text-lg font-semibold">
                        <AlertTriangle className="size-5 text-warning" aria-hidden />
                        Guarda estas credenciales ahora
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="shrink-0 text-sm text-muted-foreground">
                        {systemName ? `Credenciales de ${systemName}. ` : ''}
                        Es la única vez que verás el secreto. Si lo pierdes, rótalo para generar uno
                        nuevo. Compártelas con el equipo técnico por un canal seguro.
                    </DialogPrimitive.Description>

                    {/* Solo el cuerpo hace scroll vertical; el pie con la confirmación queda siempre visible. */}
                    <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-1">
                        <div className="min-w-0 space-y-1 [overflow-wrap:anywhere]">
                            <p className="text-sm font-medium">Client ID</p>
                            <CopyableId label="Client ID" value={credentials?.clientId ?? ''} />
                        </div>

                        <div className="space-y-1">
                            <p className="text-sm font-medium">Secreto</p>
                            <div className="flex min-w-0 items-start gap-2 rounded-lg border border-border bg-muted/40 p-3">
                                <code
                                    data-testid="api-user-secret"
                                    className="sentry-mask block min-w-0 flex-1 break-all font-mono text-sm"
                                >
                                    {credentials?.clientSecret}
                                </code>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    aria-label="Copiar secreto"
                                    onClick={() => {
                                        void copyToClipboard(credentials?.clientSecret ?? '');
                                    }}
                                >
                                    <Copy className="size-4" aria-hidden />
                                </Button>
                            </div>
                        </div>

                        <div className="min-w-0 space-y-1">
                            <p className="text-sm font-medium">Ejemplo de login</p>
                            <pre className="max-w-full overflow-x-auto rounded-lg bg-muted/40 p-3 font-mono text-xs">
                                {buildApiLoginCurl(credentials?.clientId ?? '', tenantId)}
                            </pre>
                            <p className="text-xs text-muted-foreground">
                                Guarda el secreto en la variable CLIENT_SECRET. El token dura 15
                                minutos y solo funciona desde las IPs permitidas.
                            </p>
                        </div>
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="api-user-credentials-saved"
                                checked={saved}
                                onCheckedChange={(v) => {
                                    setSaved(v === true);
                                }}
                            />
                            <Label htmlFor="api-user-credentials-saved">Ya los guardé</Label>
                        </div>

                        <Button
                            type="button"
                            disabled={!saved}
                            onClick={() => {
                                setSaved(false);
                                onConfirm();
                            }}
                        >
                            Cerrar
                        </Button>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
